/**
 * RunErrorTab — Callout(danger) with root-cause prose + action buttons.
 * Actions gated on actionAvailability flags.
 * If no error: EmptyState with CheckCircle.
 */
import React from 'react';
import { AlertTriangle, CheckCircle, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Can } from '../../../components/auth/Can.js';
import { Button } from '../../../components/ui/Button.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { Callout } from '../../../components/ui/Callout.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import { type DebugPayload, findFailedStep } from './run-detail-types.js';

interface RunErrorTabProps {
  payload: DebugPayload;
  onAction: (action: string, stepId?: string) => void;
  actionPending: boolean;
}

export const RunErrorTab: React.FC<RunErrorTabProps> = ({ payload, onAction, actionPending }) => {
  const { run, graph, actionAvailability } = payload;
  const failedStep = findFailedStep(graph.steps);

  const hasError = !!(run.error || failedStep);
  const canRetryFromStep =
    (actionAvailability['retry_from_step'] ?? actionAvailability['retryFromStep'] ?? false) ||
    (actionAvailability['retry_failed_step'] ?? actionAvailability['retryFailedStep'] ?? false);

  if (!hasError) {
    return (
      <div className="py-8">
        <EmptyState icon={CheckCircle} title="No errors" description="This run completed without failures." />
      </div>
    );
  }

  const errorTitle = failedStep
    ? `Step "${failedStep.stepName}" failed${failedStep.error ? ` — ${failedStep.error}` : ''}`
    : `Run failed${run.error ? ` — ${run.error}` : ''}`;

  const retryCount = failedStep?.retryCount ?? 0;

  return (
    <div className="space-y-4">
      <Callout
        tone="danger"
        icon={AlertTriangle}
        title={errorTitle}
        actions={
          <div className="flex flex-wrap gap-2">
            {canRetryFromStep && failedStep && (
              // The actions route requires CanManageWorkflows.
              <Can policy="CanManageWorkflows">
                <Button
                  size="sm"
                  variant="danger"
                  loading={actionPending}
                  onClick={() =>
                    onAction(
                      (actionAvailability['retry_from_step'] ?? actionAvailability['retryFromStep'])
                        ? 'retry_from_step'
                        : 'retry_failed_step',
                      failedStep.stepId ?? failedStep.id,
                    )
                  }
                >
                  <RotateCw size={12} aria-hidden="true" />
                  Re-run from {failedStep.stepName}
                </Button>
              </Can>
            )}
            <Link
              to="/agents/libraries/model-runtime-policies"
              className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
            >
              Open runtime policy
            </Link>
          </div>
        }
      >
        {retryCount > 0 && (
          <p className="mt-0.5">
            {retryCount} {retryCount === 1 ? 'retry' : 'retries'} exhausted.{' '}
            {failedStep?.error && `Error: ${failedStep.error}. `}
            Consider reviewing the runtime policy or using chunked execution.
          </p>
        )}
        {!retryCount && run.error && <p className="mt-0.5">{run.error}</p>}
      </Callout>
    </div>
  );
};
